import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProductServiceSchema, type ProductServiceDto } from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { ConfirmDialog, Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';

export const PRODUCTS_SERVICES_PATH = '/api/v1/organization/products-services';
const PATH = PRODUCTS_SERVICES_PATH;
const PAGE_SIZE = 50;

/**
 * Products and services (ERD §4.3; FRD-CORE-002): the minimum the sources support — a code, a
 * name, a description and the Active / Inactive status. No transaction refers to them in this
 * release, and no price, account mapping or catalogue exists. Filters and the page are server-side
 * and kept in the URL.
 */
export function ProductsServicesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'organization.product_service:create');
  const canChange = hasCapability(session.data, 'organization.product_service:change');
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [dialog, setDialog] = useState<
    'new' | { kind: 'edit' | 'status'; item: ProductServiceDto } | null
  >(null);
  const status = params.get('status') ?? 'active';
  const term = params.get('search') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: 'status' | 'search' | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' && key !== 'status') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: 'code',
  });
  if (status !== 'all') query.set('filter[status]', status);
  if (term !== '') query.set('filter[search]', term);
  const items = useQuery({
    queryKey: ['products-services', query.toString()],
    queryFn: () => getPage(`${PATH}?${query.toString()}`, ProductServiceSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Products & services"
        description="What TAQON offers, as a master record. An employee's page says which ones they work on."
        actions={
          <>
            <ExportButton
              recordType="organization.product_service"
              href={exportHref(PATH, query)}
            />
            <ImportButton recordType="organization.product_service" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('new');
                }}
              >
                New product or service
              </Button>
            )}
          </>
        }
      />
      <SearchFilterBar
        aria-label="Product and service filters"
        label="Search by code or name"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== term) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={status === 'all' ? '' : status}
          onChange={(e) => {
            set('status', e.target.value === '' ? 'all' : e.target.value);
          }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
      </SearchFilterBar>
      {items.isError && <ErrorState message={items.error.message} />}
      <Section>
        {items.isPending && <LoadingState label="Loading products and services" />}
        {items.data?.items.length === 0 && <EmptyState title="No products or services found" />}
        {items.data !== undefined && items.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Products and services">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  {canChange && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.data.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/organization/products-services/${item.id}`}>
                        {item.code}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.description ?? '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={item.status} />
                    </TableCell>
                    {canChange && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          aria-label={`Edit ${item.code}`}
                          onClick={(event) => {
                            event.currentTarget.focus();
                            setDialog({ kind: 'edit', item });
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color={item.status === 'active' ? 'warning' : 'primary'}
                          aria-label={`${item.status === 'active' ? 'Deactivate' : 'Activate'} ${item.code}`}
                          onClick={(event) => {
                            event.currentTarget.focus();
                            setDialog({ kind: 'status', item });
                          }}
                        >
                          {item.status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={items.data.total}
              noun="record(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {dialog === 'new' && (
        <ProductServiceDialog
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog !== null && dialog !== 'new' && dialog.kind === 'edit' && (
        <ProductServiceDialog
          item={dialog.item}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog !== null && dialog !== 'new' && dialog.kind === 'status' && (
        <ProductServiceStatusDialog
          item={dialog.item}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Stack>
  );
}

/** Create (no `item`) or edit name and description. The code is the permanent identifier. */
export function ProductServiceDialog({
  item,
  onClose,
}: {
  item?: ProductServiceDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const body: Record<string, string | null> = {};
  if (item === undefined) {
    body.code = code.trim();
    body.name = name.trim();
    if (description.trim() !== '') body.description = description.trim();
  } else {
    if (name.trim() !== item.name) body.name = name.trim();
    if (description.trim() !== (item.description ?? '')) {
      body.description = description.trim() === '' ? null : description.trim();
    }
  }
  const save = useMutation({
    mutationFn: () =>
      item === undefined
        ? postJson(PATH, body, ProductServiceSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${PATH}/${item.id}`,
            body,
            ProductServiceSchema,
            session.data?.csrfToken ?? '',
            item.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products-services'] });
      // The record page's history shows the change at once.
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={item === undefined ? 'New product or service' : `Edit ${item.code}`}
      submitLabel={item === undefined ? 'Create' : 'Save changes'}
      canSubmit={
        name.trim() !== '' &&
        (item !== undefined || code.trim() !== '') &&
        Object.keys(body).length > 0
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {item === undefined ? (
        <TextField
          label="Code"
          required
          autoFocus
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
          }}
          helperText="Upper case, up to 20 characters, starting with a letter."
        />
      ) : (
        <TextField label="Code" value={item.code} disabled />
      )}
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
    </FormDialog>
  );
}

export function ProductServiceStatusDialog({
  item,
  onClose,
}: {
  item: ProductServiceDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = item.status === 'active' ? 'inactive' : 'active';
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${PATH}/${item.id}`,
        { status: next },
        ProductServiceSchema,
        session.data?.csrfToken ?? '',
        item.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products-services'] });
      // The record page's history shows the change at once.
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      title={next === 'inactive' ? `Deactivate ${item.code}` : `Activate ${item.code}`}
      message={
        next === 'inactive'
          ? `Deactivate ${item.name}? It stays in history but is marked as no longer offered.`
          : `Activate ${item.name} again?`
      }
      confirmLabel={next === 'inactive' ? 'Deactivate' : 'Activate'}
      pending={save.isPending}
      error={save.isError ? save.error.message : null}
      onConfirm={() => {
        save.mutate();
      }}
      onClose={onClose}
    />
  );
}
